/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useState } from 'react'

import { FaCompress, FaExpand } from 'react-icons/fa'

import { Button } from '@/components/ui'

/** Toggles the whole app in and out of the browser's full screen mode. Desktop only. */
export const FullscreenButton: React.FC = () => {
  const [isAppFullscreen, setIsAppFullscreen] = useState(false)

  const toggleAppFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen()
        .then(() => {
          setIsAppFullscreen(true)
        })
        .catch((err: Error) => {
          console.error(`Error attempting to enable fullscreen: ${err.message}`)
        })
    } else {
      document
        .exitFullscreen()
        .then(() => {
          setIsAppFullscreen(false)
        })
        .catch((err: Error) => {
          console.error(`Error attempting to exit fullscreen: ${err.message}`)
        })
    }
  }, [])

  // Also covers leaving full screen with Esc, which bypasses the button.
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsAppFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  return (
    <div className="hidden lg:flex items-center" data-testid="fullscreen-button">
      <Button
        size="sm"
        variation="ghost"
        handleClick={toggleAppFullscreen}
        ariaLabel={isAppFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        className="hover:bg-base-300/10 transition-colors hover:shadow-xs h-10 w-10 rounded-full bg-base-300/20 shadow-md border-none"
      >
        {isAppFullscreen ? (
          <FaCompress className="h-4 w-4 text-accent" />
        ) : (
          <FaExpand className="h-4 w-4 text-accent" />
        )}
      </Button>
    </div>
  )
}
